/**
 * Email categories: the account-level switches a person uses to decide which
 * Spyglasses emails they receive, and how per-property notification types map
 * onto them.
 *
 * Two layers, deliberately separate:
 *  - The property's notification setting (per type) decides the AUDIENCE: who
 *    on the team, plus any outside addresses, an alert is meant for.
 *  - The member's own category preference (this file's vocabulary) is the
 *    SOURCE OF TRUTH for whether they receive it. Admins can't override it.
 *
 * Pure and browser-safe: string literals and types only, no database import.
 * The enum values mirror `enum EmailCategory` and `enum PropertyNotificationType`
 * in the Prisma schema.
 */

import type {
	EmailCategoryValue,
	PropertyNotificationTypeValue,
} from "@repo/database/types";

export type EmailCategoryKey = EmailCategoryValue;
export type NotificationTypeKey = PropertyNotificationTypeValue;

/** URL-safe identifier for a category, used in `?optOut=` and API payloads. */
export type EmailCategorySlug =
	| "property_warnings"
	| "scheduled_reports"
	| "weekly_summary"
	| "milestones";

/** Anchor ids of the groups on the account notifications page. */
export type EmailPreferenceGroupId =
	| "property-warnings"
	| "reports-and-summaries"
	| "from-spyglasses"
	| "account-and-billing";

export interface EmailCategoryConfig {
	key: EmailCategoryKey;
	slug: EmailCategorySlug;
	/** The group (and anchor) the switch lives in on the account page. */
	group: EmailPreferenceGroupId;
	/** The per-property notification types this category gates. */
	notificationTypes: NotificationTypeKey[];
}

export const EMAIL_CATEGORY_KEYS = [
	"PROPERTY_WARNINGS",
	"SCHEDULED_REPORTS",
	"WEEKLY_SUMMARY",
	"MILESTONES",
] as const satisfies readonly EmailCategoryKey[];

export const NOTIFICATION_TYPE_KEYS = [
	"WRONG_DOMAIN",
	"NEGATIVE_MESSAGE",
	"SCHEDULED_REPORT",
	"WEEKLY_SUMMARY",
	"MILESTONE",
] as const satisfies readonly NotificationTypeKey[];

export const EMAIL_CATEGORY_CONFIG: Record<
	EmailCategoryKey,
	EmailCategoryConfig
> = {
	PROPERTY_WARNINGS: {
		key: "PROPERTY_WARNINGS",
		slug: "property_warnings",
		group: "property-warnings",
		notificationTypes: ["WRONG_DOMAIN", "NEGATIVE_MESSAGE"],
	},
	SCHEDULED_REPORTS: {
		key: "SCHEDULED_REPORTS",
		slug: "scheduled_reports",
		group: "reports-and-summaries",
		notificationTypes: ["SCHEDULED_REPORT"],
	},
	WEEKLY_SUMMARY: {
		key: "WEEKLY_SUMMARY",
		slug: "weekly_summary",
		group: "reports-and-summaries",
		notificationTypes: ["WEEKLY_SUMMARY"],
	},
	MILESTONES: {
		key: "MILESTONES",
		slug: "milestones",
		group: "reports-and-summaries",
		notificationTypes: ["MILESTONE"],
	},
};

/** Which account-level category gates each per-property notification type. */
export const NOTIFICATION_TYPE_CATEGORY: Record<
	NotificationTypeKey,
	EmailCategoryKey
> = {
	WRONG_DOMAIN: "PROPERTY_WARNINGS",
	NEGATIVE_MESSAGE: "PROPERTY_WARNINGS",
	SCHEDULED_REPORT: "SCHEDULED_REPORTS",
	WEEKLY_SUMMARY: "WEEKLY_SUMMARY",
	MILESTONE: "MILESTONES",
};

/** URL-safe identifier for a notification type, used in API paths. */
export type NotificationTypeSlug =
	| "wrong_domain"
	| "negative_message"
	| "scheduled_report"
	| "weekly_summary"
	| "milestone";

export const NOTIFICATION_TYPE_SLUGS: Record<
	NotificationTypeKey,
	NotificationTypeSlug
> = {
	WRONG_DOMAIN: "wrong_domain",
	NEGATIVE_MESSAGE: "negative_message",
	SCHEDULED_REPORT: "scheduled_report",
	WEEKLY_SUMMARY: "weekly_summary",
	MILESTONE: "milestone",
};

/**
 * Card anchor ids on the property Notifications tab. `#<id>` deep-links scroll
 * to the card and highlight it.
 */
export const NOTIFICATION_TYPE_ANCHORS: Record<NotificationTypeKey, string> = {
	WRONG_DOMAIN: "wrong-domain-alerts",
	NEGATIVE_MESSAGE: "negative-message-alerts",
	SCHEDULED_REPORT: "scheduled-report-emails",
	WEEKLY_SUMMARY: "weekly-summary",
	MILESTONE: "milestone-emails",
};

export function emailCategoryForNotificationType(
	type: NotificationTypeKey,
): EmailCategoryKey {
	return NOTIFICATION_TYPE_CATEGORY[type];
}

export function emailCategorySlug(
	category: EmailCategoryKey,
): EmailCategorySlug {
	return EMAIL_CATEGORY_CONFIG[category].slug;
}

/** Parse a slug (`property_warnings`) or key (`PROPERTY_WARNINGS`). */
export function parseEmailCategory(
	raw: string | null | undefined,
): EmailCategoryKey | null {
	if (!raw) return null;
	const value = raw.trim();
	for (const config of Object.values(EMAIL_CATEGORY_CONFIG)) {
		if (config.slug === value || config.key === value) return config.key;
	}
	return null;
}

/** Parse a slug (`wrong_domain`) or key (`WRONG_DOMAIN`). */
export function parseNotificationType(
	raw: string | null | undefined,
): NotificationTypeKey | null {
	if (!raw) return null;
	const value = raw.trim();
	for (const key of NOTIFICATION_TYPE_KEYS) {
		if (key === value || NOTIFICATION_TYPE_SLUGS[key] === value) return key;
	}
	return null;
}

// ---------------------------------------------------------------------------
// Launch window
// ---------------------------------------------------------------------------

/**
 * When per-property notifications (default on for everyone) launched. Drives
 * the "New: …" line in warning emails and the "moved to Notifications" callout
 * on the Reports tab. Edit if the launch date moves.
 */
export const NOTIFICATIONS_V2_LAUNCH_DATE = new Date(
	"2026-10-13T00:00:00.000Z",
);

/** Days after launch that warning emails include the "New: …" line. */
export const NOTIFICATIONS_V2_EMAIL_NOTE_DAYS = 30;

/** Days after launch that the Reports tab shows the "moved" callout. */
export const NOTIFICATIONS_V2_REPORTS_CALLOUT_DAYS = 60;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * True when `date` falls in `[launch, launch + days)`. Before the launch date
 * it is false: nothing has moved yet, so there is nothing to announce.
 */
export function isWithinLaunchWindow(
	date: Date,
	days: number,
	launchDate: Date = NOTIFICATIONS_V2_LAUNCH_DATE,
): boolean {
	const t = date.getTime();
	const start = launchDate.getTime();
	return t >= start && t < start + days * DAY_MS;
}
